import moment from 'moment'

export function timeDisplay(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const m = typeof value === 'number' ? moment.utc(new Date(value)) : moment.utc(value as any)
  return m.isValid() ? m.format('h:mm a') : ''
}

export function hourMinutesFromIso(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null
  const parts = value.split('T')
  if (parts.length < 2) return null
  const time = parts[1].split(':')
  if (time.length < 2) return null
  const hh = time[0]
  const mm = time[1]
  if (!/^\d{2}$/.test(hh) || !/^\d{2}$/.test(mm)) return null
  return `${hh}:${mm}`
}

export interface AddressValidity {
  isValid: boolean
  missingFields: string[]
}

/**
 * Address format used across this codebase:
 *   ${apt}***${street}***${unit}***${city}***${postalcode}***${province}***${country}
 * Indices: 0=apt, 1=street, 2=unit, 3=city, 4=postalcode, 5=province, 6=country
 * Required fields are: street (1), city (3), postalcode (4), province (5).
 */
export function isAddressValid(stop: { orderType?: string; address?: string }): AddressValidity {
  if (stop?.orderType === 'HUB') return { isValid: true, missingFields: [] }
  if (!stop?.address) return { isValid: false, missingFields: ['address'] }

  const parts = stop.address.split('***')
  const street = parts[1]?.trim()
  const city = parts[3]?.trim()
  const province = parts[5]?.trim()
  const postalcode = parts[4]?.trim()

  const missing: string[] = []
  if (!street) missing.push('street')
  if (!city) missing.push('city')
  if (!province) missing.push('province')
  if (!postalcode) missing.push('postal code')
  return { isValid: missing.length === 0, missingFields: missing }
}

/**
 * Lifted from OPTIMIZER's src/utils/index.ts. Client field is stored as
 * "BusinessUnit***BusinessSubUnit"; render distinct parts as "A / B", collapse
 * identical parts to one.
 */
export function formatClientName(raw: string | null | undefined): string {
  if (!raw) return ''
  const parts = String(raw).split('***').map((p) => p.trim()).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0]
  if (parts[0].toLowerCase() === parts[1].toLowerCase()) return parts[0]
  return `${parts[0]} / ${parts[1]}`
}

/** HH:mm slots across a 24h day (default 15-min granularity) for window dropdowns. */
export function windowOptions(stepMinutes = 15): string[] {
  const out: string[] = []
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    const hh = String(Math.floor(m / 60)).padStart(2, '0')
    const mm = String(m % 60).padStart(2, '0')
    out.push(`${hh}:${mm}`)
  }
  return out
}

/** Build the optimizer ISO window string from a date (YYYY-MM-DD) and HH:mm. */
export function isoFromDateAndHHmm(date: string, hhmm: string): string {
  return `${date}T${hhmm}:00.000Z`
}

/** "Qty X Product, …" from a stop's productJson; falls back to "<n> items". */
export function formatProducts(stop: { productJson?: any[]; productCount?: number }): string {
  if (Array.isArray(stop.productJson) && stop.productJson.length) {
    return stop.productJson.map((p) => `${p.Quantity} X ${p.Product}`).join(', ')
  }
  return stop.productCount ? `${stop.productCount} items` : ''
}
